/**
 * @file BackgroundView.ts
 * @role Full-bleed cover background renderer. It receives a texture by asset id through an
 *       injected resolver, keeping Pixi asset access at the composition boundary.
 * @owns Background sprite sizing and focal-point cover math.
 * @reads LayoutContext and asset id.
 * @neverDoes Load assets. Decide layout mode. Read window.
 * @invariants 14, 15.
 * @see src/core/assets/AssetService.ts
 */
import { Container, Graphics, Sprite } from 'pixi.js';
import type { LayoutContext } from '@core/layout/LayoutContext';
import type { TextureResolver } from '@core/assets/TextureResolver';

export type { TextureResolver };

export class BackgroundView extends Container {
  private readonly fallback = new Graphics();
  private readonly sprite = new Sprite();
  private currentAssetId = '';

  constructor(
    private readonly resolveTexture: TextureResolver,
    private readonly fallbackColor = 0x0a0a12,
  ) {
    super();
    this.addChild(this.fallback, this.sprite);
    this.sprite.visible = false;
  }

  applyLayout(layout: LayoutContext, assetId: string, focalX = 0.5, focalY = 0.5): void {
    this.fallback
      .clear()
      .rect(0, 0, layout.width, layout.height)
      .fill({ color: this.fallbackColor });
    // Retry the resolver when a deferred orientation bundle has just finished loading.
    if (assetId !== this.currentAssetId || !this.sprite.visible) {
      this.currentAssetId = assetId;
      const texture = this.resolveTexture(assetId);
      if (texture) {
        this.sprite.texture = texture;
        this.sprite.visible = true;
      } else {
        this.sprite.visible = false;
      }
    }
    if (!this.sprite.visible) return;

    const sourceW = this.sprite.texture.width || layout.width;
    const sourceH = this.sprite.texture.height || layout.height;
    const scale = Math.max(layout.width / sourceW, layout.height / sourceH);
    this.sprite.scale.set(scale);
    this.sprite.position.set(
      (layout.width - sourceW * scale) * focalX,
      (layout.height - sourceH * scale) * focalY,
    );
  }

  get ready(): boolean {
    return this.sprite.visible;
  }
}
